<template>
    <div
        class="vrm-search--enhanced--active vrm-search-modal-container"
        @mousedown.self="closeSearch"
        @keydown.esc.prevent="closeSearch"
    >
        <div class="vrm-search-modal-wrapper">
            <div class="vrm-search-modal-header">
                <div>
                    <span
                        class="sg-vrm-icon_048_arrow-left-16px vrm-search__icon--enhanced--active--back"
                        @click="closeSearch"
                    ></span>
                </div>
                <div class="vrm-search-modal-form">
                    <form action>
                        <input
                            v-model="searchTerm"
                            type="search"
                            class="sg-element vrm-search__input--enhanced--active"
                            data-cy="enhanced-search-input-active"
                            :placeholder="
                                $t('nav.enhanced_search.placeholder', {
                                    i18nFallback: 'Search installations',
                                })
                            "
                            @input="() => (currentItemIndex = -1)"
                            @keydown.up.prevent="navigateItems"
                            @keydown.down.prevent="navigateItems"
                            @keydown.enter.prevent="navigateItems"
                            @keydown.tab.prevent="navigateItems"
                        />
                    </form>
                    <div class="separator"></div>
                    <span class="vrm-search__icon--enhanced--active" @click="handleSearch">
                        <Spinner v-if="loading" />
                        <span v-else class="sg-vrm-icon_121_search-24px"></span>
                    </span>
                </div>
            </div>
            <p v-if="loading" class="vrm-search-modal-length">
                {{
                    $t('nav.enhanced_search.loading', {
                        i18nFallback: 'Loading...',
                    })
                }}
            </p>
            <p v-if="!searchEmpty && !loading" class="vrm-search-modal-length">
                {{ hitsNumber }}
                {{
                    $t('nav.enhanced_search.installations_found', {
                        i18nFallback: 'installation(s) found',
                    })
                }}
            </p>
            <div class="vrm-search-modal-results">
                <VrmEnhancedSearchActiveEmpty v-if="searchEmpty" />
                <div v-else class="vrm-search-modal-results--success">
                    <div
                        class="vrm-search-modal-results--success--list"
                        @keydown.prevent="navigateItems"
                    >
                        <VrmEnhancedSearchItem
                            v-for="(installation, index) in searchResults"
                            :key="`${installation.site_id}${Date.now()}`"
                            ref="items"
                            :installation-data="installation"
                            :is-focused="currentItemIndex === index"
                        />
                        <VrmEnhancedSearchActiveNoResults
                            v-if="noResults || searchError"
                            :search-term-on-fail="searchTermOnFail"
                            :search-error="searchError"
                        />
                    </div>
                </div>
            </div>
            <div v-if="!isMobile" class="vrm-search-modal__footer">
                <div>
                    <span class="vrm-search-modal__footer-icon sg-vrm-icon_147_select_16px"></span>
                    <span>Select</span>
                </div>
                <div>
                    <span class="vrm-search-modal__footer-icon sg-vrm-icon_145_return_16px"></span>
                    <span>Open</span>
                </div>
                <div>
                    <span class="vrm-search-modal__footer-icon">Esc</span>
                    <span>Close</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Vue, Component, Watch } from 'vue-property-decorator';
import VrmEnhancedSearchItem from './VrmEnhancedSearchItem.vue';
import VrmEnhancedSearchActiveEmpty from './VrmEnhancedSearchActiveEmpty.vue';
import VrmEnhancedSearchActiveNoResults from './VrmEnhancedSearchActiveNoResults.vue';
import Spinner from '../device-controls/controls/components/Spinner.vue';
import { searchByOptions } from '@/vue/constants/search-enhanced';
import { AngularDirective, ngInject } from '@/vue/decorators/angular-interop';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';

@Component({
    components: {
        VrmEnhancedSearchItem,
        VrmEnhancedSearchActiveEmpty,
        VrmEnhancedSearchActiveNoResults,
        Spinner,
    },
    props: {
        searchResults: {
            type: Array,
            default: [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        noResults: {
            type: Boolean,
            default: false,
        },
        hitsNumber: {
            type: Number,
            default: 0,
        },
        searchError: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['on-close-search', 'on-search'],
})
@AngularLocaleBundle('nav')
@AngularDirective('vrm-enhanced-search-active')
export default class VrmEnhancedSearchActive extends Vue {
    @ngInject()
    $rootScope;

    searchTerm = '';
    searchEmpty = true;
    searchTermOnFail = ''; // used to display the search term that failed
    searchByOptions = searchByOptions; // used to display the search options in the search empty state
    currentItemIndex = -1; // used to navigate through the search results

    created() {
        // Listen for the AngularJS event
        this.unregisterClearEvent = this.$rootScope.$on('clearEvent', this.handleClearEvent);

        // Debounce function to prevent multiple API calls
        function debounce(fn, wait) {
            let timeout;
            return function(...args) {
                clearTimeout(timeout);
                timeout = setTimeout(() => {
                    fn.apply(this, args);
                }, wait);
                this._debounceTimeout = timeout;
            };
        }

        this.debouncedEmitSearch = debounce(this.emitSearch, 1000);
    }

    @Watch('searchTerm')
    onSearchTermChange() {
        this.debouncedEmitSearch();
    }

    emitSearch() {
        if (this.searchTerm.trim().length > 2) {
            this.searchEmpty = false;
            this.$emit('on-search', { searchTerm: this.searchTerm });
            this.searchTermOnFail = this.searchTerm;
        } else if (this.searchTerm.trim().length === 0) {
            this.searchEmpty = true;
            this.$emit('on-search', { searchTerm: this.searchTerm });
        }
        this.currentItemIndex = -1;
    }

    beforeDestroy() {
        this.unregisterClearEvent();
    }

    handleClearEvent() {
        this.searchTerm = '';
        this.searchEmpty = true;
        this.$emit('on-search', { searchTerm: this.searchTerm });
    }

    handleSearch() {
        clearTimeout(this._debounceTimeout);
        if (this.searchTerm === '') {
            this.searchEmpty = true;
            this.$emit('on-search', { searchTerm: this.searchTerm });
        } else {
            this.searchEmpty = false;
            this.$emit('on-search', { searchTerm: this.searchTerm });
            this.searchTermOnFail = this.searchTerm;
        }
        this.currentItemIndex = -1;
    }

    closeSearch() {
        clearTimeout(this._debounceTimeout);
        this.$emit('on-close-search', this.searchTerm);
        if (this.searchTerm === '') {
            this.searchEmpty = true;
        }
        this.currentItemIndex = -1;
    }

    changeFocus(index) {
        this.currentItemIndex = index;
    }

    navigateItems(event) {
        if (this.searchResults.length === 0) return;

        switch (event.key) {
            case 'ArrowDown':
                if (this.currentItemIndex < this.searchResults.length - 1) {
                    this.currentItemIndex++;
                } else {
                    this.currentItemIndex = 0;
                }
                this.changeFocus(this.currentItemIndex);
                break;

            case 'ArrowUp':
                this.currentItemIndex > 0 && this.currentItemIndex--;
                this.changeFocus(this.currentItemIndex);
                break;

            case 'Enter':
                if (
                    this.$refs.items &&
                    this.$refs.items[this.currentItemIndex] &&
                    this.currentItemIndex >= 0
                ) {
                    // In this case "Enter" will trigger the router
                    this.$refs.items[this.currentItemIndex].navigateToItem();
                } else if (this.currentItemIndex === -1) {
                    this.handleSearch();
                }
                break;

            case 'Tab':
                if (event.shiftKey) {
                    this.currentItemIndex > 0 && this.currentItemIndex--;
                    this.changeFocus(this.currentItemIndex);
                } else if (this.currentItemIndex < this.searchResults.length - 1) {
                    this.currentItemIndex++;
                } else if (this.currentItemIndex === -1) {
                    this.currentItemIndex = 0;
                    this.changeFocus(this.currentItemIndex);
                }
                break;
        }
    }

    get isMobile() {
        return window.innerWidth <= 750;
    }
}
</script>
